(function () {
  'use strict';

  const money = (cents) => {
    const value = (Number(cents || 0) / 100).toFixed(2);
    return `$${value}`;
  };

  const escapeHtml = (value) => String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

  const rootPath = () => (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';

  function initHeader(header) {
    if (!header || header.dataset.wantedInitialized === 'true') return;
    header.dataset.wantedInitialized = 'true';

    const spacer = document.querySelector('[data-wanted-header-spacer]');
    const overlay = header.querySelector('[data-panel-overlay]');
    const mobileMenu = header.querySelector('[data-mobile-menu]');
    const mobileMenuOpen = header.querySelector('[data-mobile-menu-open]');
    const mobileMenuClose = header.querySelector('[data-mobile-menu-close]');
    const cartDrawer = header.querySelector('[data-cart-drawer]');
    const cartOpen = header.querySelector('[data-cart-open]');
    const cartClose = header.querySelector('[data-cart-close]');
    const searchModal = header.querySelector('[data-search-modal]');
    const searchOpen = header.querySelector('[data-search-open]');
    const searchClose = header.querySelector('[data-search-close]');
    const searchInput = header.querySelector('[data-search-input]');

    const cartItems = header.querySelector('[data-cart-items]');
    const cartEmpty = header.querySelector('[data-cart-empty]');
    const cartCount = header.querySelector('[data-cart-count]');
    const cartCountLabel = header.querySelector('[data-cart-count-label]');
    const cartSubtotal = header.querySelector('[data-cart-subtotal]');
    const cartShipping = header.querySelector('[data-cart-shipping]');
    const cartEstimatedTotal = header.querySelector('[data-cart-estimated-total]');
    const checkoutButton = header.querySelector('[data-checkout-button]');

    const freeShippingWrap = header.querySelector('[data-free-shipping-wrap]');
    const freeShippingMessage = header.querySelector('[data-free-shipping-message]');
    const freeShippingPercent = header.querySelector('[data-free-shipping-percent]');
    const freeShippingFill = header.querySelector('[data-free-shipping-fill]');

    const upsellsWrap = header.querySelector('[data-upsells-wrap]');
    const upsellEyebrow = header.querySelector('[data-upsell-eyebrow]');
    const upsellHeading = header.querySelector('[data-upsell-heading]');
    const upsellTrack = header.querySelector('[data-upsell-track]');
    const upsellPrev = header.querySelector('[data-upsell-prev]');
    const upsellNext = header.querySelector('[data-upsell-next]');

    const discountInput = header.querySelector('[data-discount-code]');
    const discountApply = header.querySelector('[data-discount-apply]');
    const discountNote = header.querySelector('[data-discount-note]');

    const freeShippingThreshold = Number(header.dataset.freeShippingThreshold || 5000);
    const shippingCost = Number(header.dataset.shippingCost || 500);
    const autoUpsellLimit = Math.max(0, Number(header.dataset.autoUpsellLimit || 4));

    let currentCart = null;
    let activeDiscountCode = '';
    let autoUpsellAnchorProductId = '';

    function updateSpacer() {
      if (!spacer) return;
      const height = Math.ceil(header.getBoundingClientRect().height || 0);
      spacer.style.height = `${height}px`;
      document.documentElement.style.setProperty('--wanted-header-height', `${height}px`);
    }

    function lockBody() {
      document.body.classList.add('wanted-panel-open');
    }

    function unlockBody() {
      if (
        !mobileMenu?.classList.contains('is-active') &&
        !cartDrawer?.classList.contains('is-active') &&
        !searchModal?.classList.contains('is-active')
      ) {
        document.body.classList.remove('wanted-panel-open');
      }
    }

    function closeAll() {
      mobileMenu?.classList.remove('is-active');
      mobileMenu?.setAttribute('aria-hidden', 'true');
      mobileMenuOpen?.setAttribute('aria-expanded', 'false');

      cartDrawer?.classList.remove('is-active');
      cartDrawer?.setAttribute('aria-hidden', 'true');
      cartOpen?.setAttribute('aria-expanded', 'false');

      searchModal?.classList.remove('is-active');
      searchModal?.setAttribute('aria-hidden', 'true');

      overlay?.classList.remove('is-active');
      document.body.classList.remove('wanted-panel-open');
    }

    function openMobileMenu() {
      closeAll();
      mobileMenu?.classList.add('is-active');
      mobileMenu?.setAttribute('aria-hidden', 'false');
      mobileMenuOpen?.setAttribute('aria-expanded', 'true');
      overlay?.classList.add('is-active');
      lockBody();
    }

    async function openCart() {
      closeAll();
      cartDrawer?.classList.add('is-active');
      cartDrawer?.setAttribute('aria-hidden', 'false');
      cartOpen?.setAttribute('aria-expanded', 'true');
      overlay?.classList.add('is-active');
      lockBody();
      await refreshCart();
    }

    function openSearch() {
      closeAll();
      searchModal?.classList.add('is-active');
      searchModal?.setAttribute('aria-hidden', 'false');
      lockBody();
      window.setTimeout(() => searchInput?.focus(), 120);
    }

    async function requestCart() {
      const response = await fetch(`${rootPath()}cart.js`, {
        method: 'GET',
        headers: { Accept: 'application/json' },
        credentials: 'same-origin'
      });
      if (!response.ok) throw new Error('Unable to load cart.');
      return response.json();
    }

    function renderCartItems(cart) {
      if (!cartItems || !cartEmpty) return;

      if (!cart.items || cart.items.length === 0) {
        cartItems.innerHTML = '';
        cartItems.hidden = true;
        cartEmpty.hidden = false;
        return;
      }

      cartItems.hidden = false;
      cartEmpty.hidden = true;

      cartItems.innerHTML = cart.items.map((item, index) => {
        const image = item.image
          ? `<img class="wanted-cart-item__image" src="${escapeHtml(item.image)}" alt="${escapeHtml(item.product_title)}" loading="lazy">`
          : '';

        const variant = item.variant_title && item.variant_title !== 'Default Title'
          ? `<p class="wanted-cart-item__variant">${escapeHtml(item.variant_title)}</p>`
          : '';

        return `
          <article class="wanted-cart-item" data-cart-line="${index + 1}">
            <div class="wanted-cart-item__image-wrap">${image}</div>
            <div>
              <h3 class="wanted-cart-item__title">${escapeHtml(item.product_title)}</h3>
              ${variant}
              <div class="wanted-cart-item__price">${money(item.final_line_price)}</div>
              <div class="wanted-cart-item__controls" aria-label="Quantity controls">
                <button type="button" data-line-decrease="${index + 1}" aria-label="Decrease quantity">−</button>
                <span>${item.quantity}</span>
                <button type="button" data-line-increase="${index + 1}" aria-label="Increase quantity">+</button>
              </div>
            </div>
            <button class="wanted-cart-item__remove" type="button" data-line-remove="${index + 1}" aria-label="Remove ${escapeHtml(item.product_title)}">
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 7h16M9 7V4h6v3M7 7l1 13h8l1-13M10 11v5M14 11v5"/></svg>
            </button>
          </article>
        `;
      }).join('');
    }

    function updateShipping(cart) {
      const subtotal = Number(cart.total_price || 0);
      const threshold = Math.max(0, freeShippingThreshold);
      const remaining = Math.max(0, threshold - subtotal);
      const unlocked = threshold === 0 || subtotal >= threshold;
      const shipping = cart.item_count > 0 && !unlocked ? shippingCost : 0;
      const estimatedTotal = subtotal + shipping;
      const progress = threshold > 0 ? Math.min(100, Math.max(0, (subtotal / threshold) * 100)) : 100;

      if (freeShippingMessage) {
        freeShippingMessage.textContent = unlocked
          ? '🎉 You unlocked FREE DELIVERY!'
          : `You're ${money(remaining)} away from FREE DELIVERY`;
      }

      if (freeShippingPercent) freeShippingPercent.textContent = `${Math.round(progress)}%`;
      if (freeShippingFill) freeShippingFill.style.width = `${progress}%`;
      freeShippingWrap?.classList.toggle('is-unlocked', unlocked);

      if (cartSubtotal) cartSubtotal.textContent = money(subtotal);
      if (cartShipping) cartShipping.textContent = cart.item_count === 0 ? '—' : (unlocked ? 'FREE' : money(shipping));
      if (cartEstimatedTotal) cartEstimatedTotal.textContent = money(estimatedTotal);

      if (upsellEyebrow) {
        upsellEyebrow.textContent = cart.item_count === 0 ? 'Popular finds' : (unlocked ? 'You may also like' : 'Free delivery is close');
      }

      if (upsellHeading) {
        upsellHeading.textContent = cart.item_count === 0
          ? 'Start with one of these'
          : (unlocked ? 'Add one more find to your order' : `Only ${money(remaining)} away — add one more find`);
      }
    }

    function removeAutoUpsells() {
      if (!upsellTrack) return;
      upsellTrack.querySelectorAll('[data-upsell-source="auto"]').forEach(card => card.remove());
    }

    function getManualProductIds() {
      return new Set(Array.from(header.querySelectorAll('[data-upsell-card][data-upsell-source="manual"]')).map(card => String(card.dataset.productId || '')));
    }

    function buildAutoUpsellCard(product) {
      const variant = Array.isArray(product.variants)
        ? (product.variants.find(item => item.available) || product.variants[0])
        : null;
      if (!variant) return '';

      const image = product.featured_image
        ? `<img class="wanted-upsell-card__image" src="${escapeHtml(product.featured_image)}" alt="${escapeHtml(product.title)}" loading="lazy">`
        : '<div class="wanted-upsell-card__placeholder">No image</div>';

      const variantControl = Array.isArray(product.variants) && product.variants.length > 1
        ? `<label class="wanted-upsell-card__variant-label">Choose option</label>
           <select class="wanted-upsell-card__variant" data-upsell-variant>
             ${product.variants.map(item => `<option value="${item.id}" data-price="${item.price}" ${!item.available ? 'disabled' : ''} ${item.id === variant.id ? 'selected' : ''}>${escapeHtml(item.title)}${!item.available ? ' — Sold out' : ''}</option>`).join('')}
           </select>`
        : `<input type="hidden" value="${variant.id}" data-upsell-variant>`;

      return `
        <article class="wanted-upsell-card" data-upsell-card data-upsell-source="auto" data-product-id="${product.id}">
          <a href="${escapeHtml(product.url || '#')}" class="wanted-upsell-card__image-link" tabindex="-1" aria-hidden="true">${image}</a>
          <div class="wanted-upsell-card__content">
            <a href="${escapeHtml(product.url || '#')}" class="wanted-upsell-card__title">${escapeHtml(product.title)}</a>
            ${variantControl}
            <div class="wanted-upsell-card__bottom">
              <span class="wanted-upsell-card__price" data-upsell-price>${money(variant.price)}</span>
              <button type="button" class="wanted-upsell-card__add" data-upsell-add ${!variant.available ? 'disabled' : ''}>+ Add</button>
            </div>
          </div>
        </article>`;
    }

    async function loadAutomaticUpsells(cart) {
      if (!upsellTrack || autoUpsellLimit <= 0 || !cart?.items?.length) {
        removeAutoUpsells();
        autoUpsellAnchorProductId = '';
        return;
      }

      const anchorId = String(cart.items[0].product_id || '');
      if (!anchorId) return;
      if (anchorId === autoUpsellAnchorProductId && upsellTrack.querySelector('[data-upsell-source="auto"]')) return;

      autoUpsellAnchorProductId = anchorId;
      removeAutoUpsells();

      try {
        const response = await fetch(`${rootPath()}recommendations/products.json?product_id=${encodeURIComponent(anchorId)}&limit=${autoUpsellLimit}&intent=related`, {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin'
        });
        if (!response.ok) return;
        const data = await response.json();
        const products = Array.isArray(data.products) ? data.products : [];
        const cartProductIds = new Set((cart.items || []).map(item => String(item.product_id)));
        const manualIds = getManualProductIds();

        products.forEach(product => {
          if (!product || cartProductIds.has(String(product.id)) || manualIds.has(String(product.id))) return;
          const html = buildAutoUpsellCard(product);
          if (html) upsellTrack.insertAdjacentHTML('beforeend', html);
        });
      } catch (error) {
        console.warn('[Wanted cart] automatic recommendations unavailable:', error);
      }
    }

    function updateUpsells(cart) {
      if (!upsellsWrap) return;

      const productIdsInCart = new Set((cart.items || []).map(item => String(item.product_id)));
      const cards = Array.from(header.querySelectorAll('[data-upsell-card]'));
      let visibleCount = 0;

      cards.forEach(card => {
        const shouldHide = productIdsInCart.has(String(card.dataset.productId));
        card.hidden = shouldHide;
        if (!shouldHide) visibleCount += 1;
      });

      upsellsWrap.hidden = visibleCount === 0 && autoUpsellLimit <= 0;

      if (visibleCount > 0 && upsellTrack) {
        window.requestAnimationFrame(() => upsellTrack.scrollTo({ left: 0, behavior: 'smooth' }));
      }
    }

    function isCheckoutLocked() {
      return !checkoutButton || checkoutButton.dataset.checkoutLocked !== 'false';
    }

    function updateCartHeader(cart) {
      const count = Number(cart.item_count || 0);
      if (cartCount) cartCount.textContent = String(count);
      if (cartCountLabel) cartCountLabel.textContent = `${count} ${count === 1 ? 'item' : 'items'}`;
      if (checkoutButton) {
        const shouldDisableCheckout = count === 0 || isCheckoutLocked();
        checkoutButton.disabled = shouldDisableCheckout;
        checkoutButton.setAttribute('aria-disabled', shouldDisableCheckout ? 'true' : 'false');
      }
    }

    async function refreshCart() {
      try {
        currentCart = await requestCart();
        updateCartHeader(currentCart);
        renderCartItems(currentCart);
        updateShipping(currentCart);
        await loadAutomaticUpsells(currentCart);
        updateUpsells(currentCart);
        document.dispatchEvent(new CustomEvent('wanted:cart-refreshed', { detail: currentCart }));
        return currentCart;
      } catch (error) {
        console.error('[Wanted cart] refresh failed:', error);
        return null;
      }
    }

    async function addVariant(variantId, quantity = 1, properties = {}) {
      if (!variantId) throw new Error('Missing variant ID.');

      const response = await fetch(`${rootPath()}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        credentials: 'same-origin',
        body: JSON.stringify({
          items: [{
            id: Number(variantId),
            quantity: Math.max(1, Number(quantity || 1)),
            properties: properties || {}
          }]
        })
      });

      if (!response.ok) {
        let message = 'Unable to add this item.';
        try {
          const error = await response.json();
          message = error.description || error.message || message;
        } catch (_) {}
        throw new Error(message);
      }

      const result = await response.json();
      await refreshCart();
      document.dispatchEvent(new CustomEvent('cart:updated'));
      return result;
    }

    async function changeLine(line, quantity) {
      const response = await fetch(`${rootPath()}cart/change.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        credentials: 'same-origin',
        body: JSON.stringify({ line: Number(line), quantity: Math.max(0, Number(quantity)) })
      });

      if (!response.ok) throw new Error('Unable to update cart.');
      currentCart = await response.json();
      updateCartHeader(currentCart);
      renderCartItems(currentCart);
      updateShipping(currentCart);
      await loadAutomaticUpsells(currentCart);
      updateUpsells(currentCart);
      document.dispatchEvent(new CustomEvent('cart:updated'));
      return currentCart;
    }

    function getCurrentLineQuantity(line) {
      if (!currentCart || !currentCart.items || !currentCart.items[line - 1]) return 0;
      return Number(currentCart.items[line - 1].quantity || 0);
    }

    mobileMenuOpen?.addEventListener('click', openMobileMenu);
    mobileMenuClose?.addEventListener('click', closeAll);
    cartOpen?.addEventListener('click', openCart);
    cartClose?.addEventListener('click', closeAll);
    searchOpen?.addEventListener('click', openSearch);
    searchClose?.addEventListener('click', closeAll);
    overlay?.addEventListener('click', closeAll);

    searchModal?.addEventListener('click', (event) => {
      if (event.target === searchModal) closeAll();
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') closeAll();
    });

    mobileMenu?.querySelectorAll('a').forEach(link => link.addEventListener('click', closeAll));

    cartItems?.addEventListener('click', async (event) => {
      const decrease = event.target.closest('[data-line-decrease]');
      const increase = event.target.closest('[data-line-increase]');
      const remove = event.target.closest('[data-line-remove]');

      try {
        if (decrease) {
          const line = Number(decrease.dataset.lineDecrease);
          await changeLine(line, getCurrentLineQuantity(line) - 1);
        } else if (increase) {
          const line = Number(increase.dataset.lineIncrease);
          await changeLine(line, getCurrentLineQuantity(line) + 1);
        } else if (remove) {
          await changeLine(Number(remove.dataset.lineRemove), 0);
        }
      } catch (error) {
        console.error('[Wanted cart] quantity update failed:', error);
      }
    });

    header.addEventListener('change', (event) => {
      const control = event.target.closest('[data-upsell-variant]');
      if (!control) return;
      const card = control.closest('[data-upsell-card]');
      const priceElement = card?.querySelector('[data-upsell-price]');
      const selectedOption = control.tagName === 'SELECT' ? control.selectedOptions[0] : null;
      if (priceElement && selectedOption?.dataset.price) {
        priceElement.textContent = money(Number(selectedOption.dataset.price));
      }
    });

    header.addEventListener('click', async (event) => {
      const addButton = event.target.closest('[data-upsell-add]');
      if (!addButton) return;

      const card = addButton.closest('[data-upsell-card]');
      const variantControl = card?.querySelector('[data-upsell-variant]');
      const variantId = variantControl?.value;
      if (!variantId) return;

      const originalText = addButton.textContent;
      addButton.disabled = true;
      addButton.textContent = 'Adding...';

      try {
        await addVariant(variantId, 1);
        addButton.textContent = 'Added';
        window.setTimeout(() => {
          if (document.contains(addButton)) {
            addButton.textContent = originalText;
            addButton.disabled = false;
          }
        }, 900);
      } catch (error) {
        console.error('[Wanted cart] upsell add failed:', error);
        addButton.textContent = 'Try again';
        window.setTimeout(() => {
          if (document.contains(addButton)) {
            addButton.textContent = originalText;
            addButton.disabled = false;
          }
        }, 1400);
      }
    });

    const scrollUpsells = (direction) => {
      if (!upsellTrack) return;
      const firstVisible = Array.from(upsellTrack.querySelectorAll('[data-upsell-card]')).find(card => !card.hidden);
      const step = firstVisible ? firstVisible.getBoundingClientRect().width + 10 : upsellTrack.clientWidth * 0.9;
      upsellTrack.scrollBy({ left: step * direction, behavior: 'smooth' });
    };

    upsellPrev?.addEventListener('click', () => scrollUpsells(-1));
    upsellNext?.addEventListener('click', () => scrollUpsells(1));

    discountApply?.addEventListener('click', () => {
      activeDiscountCode = (discountInput?.value || '').trim();
      if (!discountNote) return;

      if (activeDiscountCode) {
        discountNote.textContent = `Code “${activeDiscountCode}” will be applied by Shopify at checkout.`;
        discountNote.style.color = 'var(--wfl-primary)';
      } else {
        discountNote.textContent = 'Enter a discount code first.';
        discountNote.style.color = 'var(--wfl-danger)';
      }
    });

    checkoutButton?.addEventListener('click', (event) => {
      if (isCheckoutLocked()) {
        event.preventDefault();
        event.stopImmediatePropagation();
        checkoutButton.disabled = true;
        checkoutButton.setAttribute('aria-disabled', 'true');
        return;
      }

      if (!currentCart || Number(currentCart.item_count || 0) === 0) return;
      const typedCode = (discountInput?.value || '').trim();
      const code = typedCode || activeDiscountCode;

      if (code) {
        window.location.href = `${rootPath()}discount/${encodeURIComponent(code)}?redirect=/checkout`;
      } else {
        window.location.href = `${rootPath()}checkout`;
      }
    });

    document.addEventListener('cart:updated', refreshCart);
    window.addEventListener('resize', updateSpacer, { passive: true });

    updateSpacer();
    refreshCart();

    window.WantedCart = window.WantedCart || {};
    window.WantedCart.add = async (variantId, quantity = 1, properties = {}) => {
      const result = await addVariant(variantId, quantity, properties);
      await openCart();
      return result;
    };
    window.WantedCart.refresh = refreshCart;
    window.WantedCart.open = openCart;
  }

  function initAll() {
    document.querySelectorAll('[data-wanted-header]').forEach(initHeader);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll, { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const header = event.target.querySelector?.('[data-wanted-header]');
    if (header) initHeader(header);
  });
})();
